import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  List,
  ListItem,
  Menu,
  MenuItem,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useMemo, useState, useEffect, useCallback } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import MenuIcon from "../../../assets/images/menu.svg";
import Bookmark from "../../../assets/images/bookmarked.svg";
import Rename from "../../../assets/images/rename.svg";
import {
  getBookmarkedPrompts,
  renameConversationV2,
  unBookmarked,
} from "Samarth Module/api/samarthAiAPI";
import ReusableSnackbar from "./ReusableSnackbar";
import { useLocation, useNavigate } from "react-router-dom";

const PinnedDialog = {
  "& .MuiDialog-paper": {
    maxWidth: "800px",
    borderRadius: "32px",
    position: "relative",
    pt: "25px",
    "&:before": {
      background:
        "linear-gradient(145.45deg, #FFEC9A -130.81%, #CCF0EB 50.06%, #00B2FF 188.34%)",
      width: "100%",
      height: "100%",
      content: '""',
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      borderRadius: "32px",
      position: "absolute",
      top: "0",
      left: "0",
      transform: "rotate(180deg)",
    },
    "&:after": {
      background:
        "linear-gradient(0deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.8) 50%, rgba(255, 255, 255, 0.6) 100%)",
      width: "100%",
      height: "100%",
      content: '""',
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      borderRadius: "32px",
      position: "absolute",
      top: "0",
      left: "0",
      transform: "rotate(180deg)",
    },
  },
  "& .MuiDialogTitle-root": {
    position: "relative",
    zIndex: 1,
    p: "0px 32px 10px 32px",
  },
  "& .MuiDialogContent-root": {
    position: "relative",
    zIndex: 1,
    p: "0px 32px",
    // height: 'calc(100vh - 225px)',
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#D9E1E7",
      borderRadius: "8px",
    },
  },
};

const DialogTitles = {
  color: "#1A2025",
  fontSize: "18px",
  lineHeight: "140%",
  fontWeight: 600,
};

const chatList = {
  "& .MuiListItem-root": {
    background: "#FFFFFF",
    borderRadius: "4px",
    p: 1,
    mb: 1,
    display: "flex",
    gap: 2,
    justifyContent: "space-between",
    transition: "all 0.5s",
    cursor: "pointer",
    "&:hover": {
      background: "#F0F7FF",
      "& .actions": {
        opacity: 1,
      },
    },
    "& .MuiFormControl-root": {
      width: "100%",
      "& .MuiInputBase-input": {
        p: "0",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent !important",
      },
      "& .MuiInputBase-root": {
        color: "#4D5F6E",
        fontSize: "14px",
        lineHeight: "140%",
        fontWeight: 400,
      },
    },
  },
  "& .actions": {
    opacity: 0,
    display: "flex",
    gap: 1,
    transition: "all 0.5s",
  },
  "& .editingItem": {
    background: "#FFFFFF !important",
    cursor: "default",
    border: "1px solid #0066CC",
    "&:hover": {
      background: "#FFFFFF !important",
    },
    "& .actions": {
      opacity: "0 !important",
      pointerEvents: "none",
    },
  },
  "& .MuiTypography-body1": {
    color: "#4D5F6E",
    fontSize: "14px",
    lineHeight: "140%",
    fontWeight: 400,
  },
};

const MenuStyles = {
  "& .MuiMenu-paper": {
    background: "linear-gradient(180deg, #C2E0FF -119.85%, #FFFFFF 100%)",
    border: "1px solid #C2E0FF",
    borderRadius: "10px",
    p: "2px",
    "& .MuiMenu-list": {
      p: 0,
      "& .MuiMenuItem-root": {
        p: "12px",
        borderBottom: "1px solid #D9E1E7",
        fontSize: "14px",
        lineHeight: "140%",
        color: "#667F93",
        borderRadius: "4px",
        "&:hover": {
          background: "#fff",
        },
        "&:last-child": {
          borderBottom: 0,
        },
        "& img": {
          mr: 1,
        },
      },
    },
  },
};

function BookmarkChatsDialog({
  open,
  onClose,
  fetchConversations,
  fetchConversationId,
  drawerClose,
  isState,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const userInfo = useMemo(
    () => JSON.parse(localStorage.getItem("UserInfo"))?.user_info,
    [],
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleRename = () => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === selectedId ? { ...item, isEditing: true } : item,
      ),
    );
    handleCloseMenu();
  };

  const handleSave = async (id) => {
    const item = items.find((i) => i.id === id);
    if (!item) return;

    const editValue = item.text;

    const payload = {
      user_id: userInfo.id,
      user_name: userInfo.name,
      user_email: userInfo.email,
      conversation_id: id,
      conversation_title: editValue,
    };

    try {
      const res = await renameConversationV2(payload);
      const { conversation_id, conversation_title } = res.data;

      setItems((prev) =>
        prev.map((c) =>
          c.id === conversation_id
            ? { ...c, isEditing: false, text: conversation_title }
            : c,
        ),
      );
      fetchConversations(1, true);
      setSuccessMessage("Chat Renamed Successfully");
      setSuccess(true);
    } catch (error) {
      console.error("Rename failed:", error);
      setError("Failed to rename title. Please try again.");
    }
  };

  const handleChangeText = (id, value) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, text: value } : item)),
    );
  };

  const handleRemove = async () => {
    const id = selectedId;

    const payload = {
      user_id: userInfo.id,
      user_name: userInfo.name,
      user_email: userInfo.email,
      conversation_id: id,
    };

    const selectedChat = items.find((item) => item.id === id);
    setItems((prev) => prev.filter((item) => item.id !== id));
    handleCloseMenu();

    try {
      await unBookmarked(payload);
      fetchConversations(1, true);
    } catch (error) {
      console.error("Unbookmark failed", error);

      setItems((prev) => [...prev, selectedChat]);
    }
  };
  const mapApiToItems = (data) => {
    return data.bookmarked_conversation.map((item) => ({
      id: item.conversation_id,
      text: item.conversation_title,
      isEditing: false,
      is_bookmarked: item.is_bookmarked,
    }));
  };
  const fetchBookmarkedChats = useCallback(async () => {
    setLoading(true);
    const payload = {
      user_id: userInfo.id,
      user_name: userInfo.name,
      user_email: userInfo.email,
    };

    try {
      const res = await getBookmarkedPrompts(payload);
      const data = await res;

      const formattedItems = mapApiToItems(data);
      setItems(formattedItems);
    } catch (error) {
      console.error("Failed to fetch bookmarks", error);
    } finally {
      setLoading(false);
    }
  }, [userInfo]);

  useEffect(() => {
    if (open) {
      fetchBookmarkedChats();
    }
  }, [open, fetchBookmarkedChats]);
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={PinnedDialog}
      >
        <DialogTitle
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography sx={DialogTitles}>BookMarked Chats</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon sx={{ color: "#0066CC" }} />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {items.length === 0 ? (
                <Typography variant="body1" align="center" mb="32px">
                  No bookmarks found
                </Typography>
              ) : (
                <List sx={chatList}>
                  {items.map((item) => (
                    <ListItem
                      key={item.id}
                      className={item.isEditing ? "editingItem" : ""}
                      onClick={() => {
                        // Don't navigate or close while editing
                        if (item.isEditing) return;

                        if (!isState) {
                          navigate(`/samarth/chat/${item.id}`, {
                            state: {
                              reset: true,
                              is_bookmarked: item.is_bookmarked,
                            },
                            replace: true,
                          });
                          fetchConversationId(item.id, 1, true, {
                            showLoader: true,
                          });
                          onClose();
                        } else {
                          navigate(location.pathname, {
                            state: {
                              reset: true,
                              conversationId: item.id,
                              is_bookmarked: item.is_bookmarked,
                            },
                            replace: true,
                          });
                          fetchConversationId(item.id, 1, true, {
                            showLoader: true,
                          });
                          drawerClose?.();
                          onClose();
                        }
                      }}
                    >
                      {!item.isEditing ? (
                        <Typography>{item.text}</Typography>
                      ) : (
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={{ width: "100%" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <TextField
                            size="small"
                            value={item.text}
                            onChange={(e) =>
                              handleChangeText(item.id, e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleSave(item.id);
                              }
                            }}
                            autoFocus
                          />
                          <IconButton
                            onClick={() => handleSave(item.id)}
                            sx={{
                              p: 0,
                              position: "absolute",
                              top: 8,
                              right: 8,
                            }}
                          >
                            <CheckIcon
                              color="primary"
                              sx={{ color: "#0066CC" }}
                            />
                          </IconButton>
                        </Box>
                      )}

                      <Box className="actions">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClick(e, item.id);
                          }}
                          sx={{ p: 0 }}
                        >
                          <Box component="img" src={MenuIcon} />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}

          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleCloseMenu}
            sx={MenuStyles}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={handleRename}>
              {" "}
              <Box component="img" src={Rename} /> Rename
            </MenuItem>
            <MenuItem onClick={handleRemove}>
              {" "}
              <Box component="img" src={Bookmark} /> Remove bookmark
            </MenuItem>
          </Menu>
        </DialogContent>
      </Dialog>
      <ReusableSnackbar
        open={success}
        message={successMessage}
        type="success"
        onClose={() => setSuccess(false)}
      />

      <ReusableSnackbar
        open={!!error}
        message={error}
        type="error"
        onClose={() => setError("")}
      />
    </>
  );
}

export default BookmarkChatsDialog;